import { useEffect, useRef, useState } from 'preact/hooks';
import type { PromptFlowState } from '../pages/ContributePage';
import styles from './StackedPromptCards.module.css';

interface StackedPromptCardsProps {
  prompts: string[];
  promptExamples?: string[];
  selectedIndex?: number | null;
  onPromptChange?: (index: number) => void;
  headerTitle?: string;
  promptStates?: Map<number, PromptFlowState>;
  focusSelectedCard?: boolean;
}

export function StackedPromptCards({
  prompts,
  promptExamples,
  selectedIndex = null,
  onPromptChange,
  headerTitle,
  promptStates,
  focusSelectedCard = false,
}: StackedPromptCardsProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedExampleIndex, setExpandedExampleIndex] = useState<number | null>(null);
  const selectedCardRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!selectedCardRef.current) return;
    selectedCardRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
    if (focusSelectedCard) selectedCardRef.current.focus();
  }, [selectedIndex, focusSelectedCard]);

  if (!prompts || prompts.length === 0) return null;

  const hasPrev = selectedIndex !== null && selectedIndex > 0;
  const hasNext = selectedIndex !== null && selectedIndex < prompts.length - 1;

  const goTo = (index: number) => {
    if (index >= 0 && index < prompts.length) {
      onPromptChange?.(index);
    }
  };

  const cards = (
    <div class={styles.deckWrap}>
      <div class={styles.deckRow}>
        {prompts.length > 1 && (
          <button
            type="button"
            class={`${styles.navArrow} ${styles.navArrowPrev}`}
            onClick={() => goTo((selectedIndex ?? 0) - 1)}
            disabled={!hasPrev}
            aria-label="Previous prompt"
          >
            &#8249;
          </button>
        )}

        <div class={styles.deck} aria-label="Writing prompts">
          {prompts.map((prompt, index) => {
            const isSelected = selectedIndex === index;
            const state = promptStates?.get(index) ?? 'unanswered';
            const isAnswered = state === 'answered';
            const isQueued = state === 'queued';
            const example = promptExamples?.[index];

            return (
              <div
                key={`${prompt}-${index}`}
                class={`${styles.card} ${isSelected ? styles.cardSelected : ''} ${isAnswered ? styles.cardFilled : ''} ${isQueued ? styles.cardQueued : ''}`}
                data-prompt-state={state}
              >
                <button
                  type="button"
                  ref={isSelected ? selectedCardRef : null}
                  class={styles.cardSelect}
                  onClick={() => onPromptChange?.(index)}
                  aria-current={isSelected ? 'step' : undefined}
                  aria-label={`Prompt ${index + 1} of ${prompts.length}, ${isSelected ? 'current, ' : ''}${state}: ${prompt}`}
                >
                  <span class={styles.cardHeader}>
                    <span class={styles.promptIndex}>Prompt {index + 1}</span>
                    {isAnswered && <span class={styles.answeredBadge}>✓ Answered</span>}
                    {isQueued && <span class={styles.queuedBadge}>◷ Waiting to sync</span>}
                  </span>

                  <span class={styles.promptText}>{prompt}</span>
                </button>

                {example && (
                  <div class={styles.exampleSection}>
                    <button
                      type="button"
                      class={styles.exampleToggle}
                      onClick={() => setExpandedExampleIndex((current) => current === index ? null : index)}
                    >
                      See an example
                    </button>
                    {expandedExampleIndex === index && (
                      <div class={styles.exampleText}>{example}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {prompts.length > 1 && (
          <button
            type="button"
            class={`${styles.navArrow} ${styles.navArrowNext}`}
            onClick={() => goTo((selectedIndex ?? 0) + 1)}
            disabled={!hasNext}
            aria-label="Next prompt"
          >
            &#8250;
          </button>
        )}
      </div>

      {prompts.length > 1 && (
        <div class={styles.positionIndicator}>
          <div class={styles.progressDots}>
            {prompts.map((_, idx) => {
              const state = promptStates?.get(idx) ?? 'unanswered';
              return (
              <button
                key={idx}
                type="button"
                class={`${styles.dot} ${idx === selectedIndex ? styles.dotActive : ''} ${state === 'answered' ? styles.dotFilled : ''} ${state === 'queued' ? styles.dotQueued : ''}`}
                onClick={() => goTo(idx)}
                aria-current={idx === selectedIndex ? 'step' : undefined}
                aria-label={`Prompt ${idx + 1} of ${prompts.length}, ${idx === selectedIndex ? 'current, ' : ''}${state}`}
              />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  if (!headerTitle) {
    return cards;
  }

  return (
    <div>
      <button
        type="button"
        class={styles.header}
        onClick={() => setIsExpanded((current) => !current)}
      >
        <span>{headerTitle}</span>
        <span class={`${styles.headerChevron} ${isExpanded ? styles.headerChevronOpen : ''}`}>&#9660;</span>
      </button>
      {isExpanded && cards}
    </div>
  );
}
